import type { AffordabilityRequest, DebtInput, GoalInput } from "@moneymap/contracts";
import {
  Button,
  Callout,
  type CalmStatus,
  Card,
  EvidenceList,
  FormField,
  Modal,
  PageHeader,
  ProgressMeter,
  SectionHeader,
  SelectInput,
  StatePanel,
  StatusBadge,
  TextInput,
} from "@moneymap/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useApi } from "../api/ApiProvider";
import type { DebtRead, GoalRead } from "../api/types";
import { QueryState } from "../components/QueryState";
import {
  formatHouseholdDate,
  formatMoney,
  parseMoneyInput,
  titleFromMachineValue,
} from "../lib/format";

type GoalFormValues = {
  name: string;
  type: GoalInput["type"];
  targetAmount: string;
  targetDate: string;
  priority: string;
  protectedFloor: string;
  note: string;
};

function GoalModal({
  isOpen,
  onClose,
  currency,
  goal,
}: {
  isOpen: boolean;
  onClose: () => void;
  currency: string;
  goal?: GoalRead;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string>();
  const { register, handleSubmit } = useForm<GoalFormValues>({
    values: {
      name: goal?.name ?? "",
      type: (goal?.type as GoalInput["type"] | undefined) ?? "emergency",
      targetAmount: goal ? formatEditableMinor(goal.targetAmountMinor) : "",
      targetDate: goal?.targetDate ?? "",
      priority: String(goal?.priority ?? 50),
      protectedFloor: goal ? formatEditableMinor(goal.protectedAmountMinor) : "",
      note: "",
    },
  });
  const mutation = useMutation({
    mutationFn: (input: GoalInput) =>
      goal ? api.updateGoal(goal.id, input, goal.version) : api.createGoal(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["goals"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      onClose();
    },
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const targetAmountMinor = parseMoneyInput(values.targetAmount);
    const protectedFloorMinor = parseMoneyInput(values.protectedFloor || "0");
    if (
      !targetAmountMinor ||
      !protectedFloorMinor ||
      targetAmountMinor.startsWith("-") ||
      protectedFloorMinor.startsWith("-")
    ) {
      setFormError("Target and protected floor must be positive or zero.");
      return;
    }
    mutation.mutate({
      name: values.name,
      type: values.type,
      targetAmountMinor,
      ...(values.targetDate ? { targetDate: values.targetDate } : {}),
      priority: Number(values.priority),
      protectedFloorMinor,
      ...(values.note ? { note: values.note } : {}),
    });
  });
  return (
    <Modal
      isOpen={isOpen}
      title={goal ? "Update goal" : "Create a goal"}
      description="Goal progress counts actual protected allocations and contributions, never intention alone."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="goal-form" isLoading={mutation.isPending}>
            {goal ? "Save changes" : "Create goal"}
          </Button>
        </>
      }
    >
      <form id="goal-form" className="form-stack" onSubmit={submit}>
        <div className="form-grid form-grid--two">
          <FormField label="Goal name" htmlFor="goal-name">
            <TextInput id="goal-name" {...register("name", { required: true })} />
          </FormField>
          <FormField label="Type" htmlFor="goal-type">
            <SelectInput id="goal-type" {...register("type")}>
              <option value="emergency">Emergency reserve</option>
              <option value="hockey_school">Hockey / school</option>
              <option value="irregular_expense">Irregular expense</option>
              <option value="debt_payoff">Debt payoff</option>
              <option value="pressure_relief">Pressure relief</option>
              <option value="custom">Custom</option>
            </SelectInput>
          </FormField>
          <FormField label={"Target amount (" + currency + ")"} htmlFor="goal-target">
            <TextInput
              id="goal-target"
              inputMode="decimal"
              {...register("targetAmount", { required: true })}
            />
          </FormField>
          <FormField
            label="Target date"
            htmlFor="goal-date"
            hint="Optional when timing is flexible."
          >
            <TextInput id="goal-date" type="date" {...register("targetDate")} />
          </FormField>
          <FormField label="Priority" htmlFor="goal-priority" hint="1 is highest; 100 is lowest.">
            <TextInput
              id="goal-priority"
              type="number"
              min={1}
              max={100}
              {...register("priority")}
            />
          </FormField>
          <FormField label={"Protected floor (" + currency + ")"} htmlFor="goal-floor">
            <TextInput id="goal-floor" inputMode="decimal" {...register("protectedFloor")} />
          </FormField>
        </div>
        <FormField label="Optional note" htmlFor="goal-note">
          <textarea id="goal-note" className="mm-input" {...register("note")} />
        </FormField>
        {formError ? (
          <Callout title="Check the goal amounts" tone="warning">
            {formError}
          </Callout>
        ) : null}
        {mutation.error ? (
          <Callout title="Goal not saved" tone="urgent">
            {mutation.error.message}
          </Callout>
        ) : null}
      </form>
    </Modal>
  );
}

function formatEditableMinor(minor: string): string {
  try {
    const value = BigInt(minor);
    const negative = value < 0n;
    const absolute = negative ? -value : value;
    return (
      (negative ? "-" : "") +
      String(absolute / 100n) +
      "." +
      String(absolute % 100n).padStart(2, "0")
    );
  } catch {
    return "";
  }
}

type DebtFormValues = {
  name: string;
  balance: string;
  statementBalance: string;
  aprPercent: string;
  minimumPayment: string;
  dueDay: string;
  strategyRank: string;
  confidence: DebtInput["confidence"];
};

function DebtModal({
  isOpen,
  onClose,
  currency,
  debt,
}: {
  isOpen: boolean;
  onClose: () => void;
  currency: string;
  debt?: DebtRead;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string>();
  const { register, handleSubmit } = useForm<DebtFormValues>({
    values: {
      name: debt?.name ?? "",
      balance: debt ? formatEditableMinor(debt.balanceMinor) : "",
      statementBalance: "",
      aprPercent: debt?.aprBasisPoints !== undefined ? String(debt.aprBasisPoints / 100) : "",
      minimumPayment: debt ? formatEditableMinor(debt.minimumPaymentMinor) : "",
      dueDay: String(debt?.dueDay ?? 1),
      strategyRank: String(debt?.strategyRank ?? 1),
      confidence: (debt?.confidence as DebtInput["confidence"] | undefined) ?? "confirmed",
    },
  });
  const mutation = useMutation({
    mutationFn: (input: DebtInput) =>
      debt ? api.updateDebt(debt.id, input, debt.version) : api.createDebt(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["debts"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      onClose();
    },
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const balanceMinor = parseMoneyInput(values.balance);
    const statementBalanceMinor = values.statementBalance
      ? parseMoneyInput(values.statementBalance)
      : null;
    const minimumPaymentMinor = parseMoneyInput(values.minimumPayment);
    if (
      !balanceMinor ||
      !minimumPaymentMinor ||
      balanceMinor.startsWith("-") ||
      minimumPaymentMinor.startsWith("-") ||
      (statementBalanceMinor?.startsWith("-") ?? false)
    ) {
      setFormError("Balances and minimum payment must be zero or positive.");
      return;
    }
    const aprBasisPoints = values.aprPercent
      ? Math.round(Number(values.aprPercent) * 100)
      : undefined;
    mutation.mutate({
      name: values.name,
      balanceMinor,
      ...(statementBalanceMinor ? { statementBalanceMinor } : {}),
      ...(aprBasisPoints !== undefined && Number.isFinite(aprBasisPoints)
        ? { aprBasisPoints }
        : {}),
      minimumPaymentMinor,
      dueDay: Number(values.dueDay),
      strategyRank: Number(values.strategyRank),
      confidence: values.confidence,
    });
  });
  return (
    <Modal
      isOpen={isOpen}
      title={debt ? "Update debt" : "Add debt"}
      description="Minimums are protected before extra payments. Missing terms remain visibly unknown."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="debt-form" isLoading={mutation.isPending}>
            {debt ? "Save changes" : "Add debt"}
          </Button>
        </>
      }
    >
      <form id="debt-form" className="form-stack" onSubmit={submit}>
        <div className="form-grid form-grid--two">
          <FormField label="Debt name" htmlFor="debt-name">
            <TextInput id="debt-name" {...register("name", { required: true })} />
          </FormField>
          <FormField label={"Current balance (" + currency + ")"} htmlFor="debt-balance">
            <TextInput
              id="debt-balance"
              inputMode="decimal"
              {...register("balance", { required: true })}
            />
          </FormField>
          <FormField label="Statement balance" htmlFor="debt-statement">
            <TextInput id="debt-statement" inputMode="decimal" {...register("statementBalance")} />
          </FormField>
          <FormField
            label="APR (%)"
            htmlFor="debt-apr"
            hint="Leave blank when unknown. IterNest Money will not estimate interest savings."
          >
            <TextInput id="debt-apr" inputMode="decimal" {...register("aprPercent")} />
          </FormField>
          <FormField label="Minimum payment" htmlFor="debt-minimum">
            <TextInput
              id="debt-minimum"
              inputMode="decimal"
              {...register("minimumPayment", { required: true })}
            />
          </FormField>
          <FormField label="Due day" htmlFor="debt-due-day">
            <TextInput id="debt-due-day" type="number" min={1} max={31} {...register("dueDay")} />
          </FormField>
          <FormField label="Strategy rank" htmlFor="debt-rank">
            <TextInput
              id="debt-rank"
              type="number"
              min={1}
              max={1000}
              {...register("strategyRank")}
            />
          </FormField>
          <FormField label="Confidence" htmlFor="debt-confidence">
            <SelectInput id="debt-confidence" {...register("confidence")}>
              <option value="confirmed">Confirmed</option>
              <option value="high">High</option>
              <option value="medium">Estimated</option>
              <option value="low">Uncertain</option>
            </SelectInput>
          </FormField>
        </div>
        {formError ? (
          <Callout title="Check the debt terms" tone="warning">
            {formError}
          </Callout>
        ) : null}
        {mutation.error ? (
          <Callout title="Debt not saved" tone="urgent">
            {mutation.error.message}
          </Callout>
        ) : null}
      </form>
    </Modal>
  );
}

type IncomeEventForm = {
  sourceName: string;
  type: "bonus" | "refund" | "other";
  expectedDate: string;
  expectedMin: string;
  expectedMax: string;
  confidence: string;
  priorityNote: string;
};

function IncomeEventModal({
  isOpen,
  onClose,
  currency,
}: {
  isOpen: boolean;
  onClose: () => void;
  currency: string;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string>();
  const { register, handleSubmit, reset } = useForm<IncomeEventForm>({
    defaultValues: {
      sourceName: "",
      type: "bonus",
      expectedDate: "",
      expectedMin: "",
      expectedMax: "",
      confidence: "medium",
      priorityNote: "",
    },
  });
  const mutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.createIncomeEvent(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["income-events"] });
      reset();
      onClose();
    },
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const expectedMinMinor = parseMoneyInput(values.expectedMin || "0");
    const expectedMaxMinor = parseMoneyInput(values.expectedMax || values.expectedMin || "0");
    if (
      !expectedMinMinor ||
      !expectedMaxMinor ||
      expectedMinMinor.startsWith("-") ||
      expectedMaxMinor.startsWith("-") ||
      BigInt(expectedMaxMinor) < BigInt(expectedMinMinor)
    ) {
      setFormError(
        "Enter a non-negative range with the high amount at least as large as the low amount.",
      );
      return;
    }
    mutation.mutate({
      sourceName: values.sourceName,
      type: values.type,
      ...(values.expectedDate ? { expectedDate: values.expectedDate } : {}),
      expectedMinMinor,
      expectedMaxMinor,
      confidence: values.confidence,
      isProvisional: true,
      allocationPriorityNote: values.priorityNote,
      currency,
    });
  });
  return (
    <Modal
      isOpen={isOpen}
      title="Plan irregular income"
      description="A bonus, refund, or other expected deposit can have a provisional job plan without being treated as covered cash."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="income-event-form" isLoading={mutation.isPending}>
            Save provisional plan
          </Button>
        </>
      }
    >
      <form id="income-event-form" className="form-stack" onSubmit={submit}>
        <div className="form-grid form-grid--two">
          <FormField label="Income name" htmlFor="income-event-name">
            <TextInput
              id="income-event-name"
              placeholder="Year-end bonus"
              {...register("sourceName", { required: true })}
            />
          </FormField>
          <FormField label="Type" htmlFor="income-event-type">
            <SelectInput id="income-event-type" {...register("type")}>
              <option value="bonus">Bonus</option>
              <option value="refund">Refund</option>
              <option value="other">Other irregular income</option>
            </SelectInput>
          </FormField>
          <FormField label="Expected date" htmlFor="income-event-date">
            <TextInput id="income-event-date" type="date" {...register("expectedDate")} />
          </FormField>
          <FormField label="Confidence" htmlFor="income-event-confidence">
            <SelectInput id="income-event-confidence" {...register("confidence")}>
              <option value="high">High</option>
              <option value="medium">Estimated</option>
              <option value="low">Uncertain</option>
            </SelectInput>
          </FormField>
          <FormField label={"Low estimate (" + currency + ")"} htmlFor="income-event-min">
            <TextInput id="income-event-min" inputMode="decimal" {...register("expectedMin")} />
          </FormField>
          <FormField label={"High estimate (" + currency + ")"} htmlFor="income-event-max">
            <TextInput id="income-event-max" inputMode="decimal" {...register("expectedMax")} />
          </FormField>
        </div>
        <FormField
          label="Priority note"
          htmlFor="income-event-priority"
          hint="IterNest Money still applies deterministic household priority order."
        >
          <textarea id="income-event-priority" className="mm-input" {...register("priorityNote")} />
        </FormField>
        {formError ? (
          <Callout title="Check the range" tone="warning">
            {formError}
          </Callout>
        ) : null}
        {mutation.error ? (
          <Callout title="Plan not saved" tone="urgent">
            {mutation.error.message}
          </Callout>
        ) : null}
      </form>
    </Modal>
  );
}

export function GoalsPage() {
  const api = useApi();
  const [goalModal, setGoalModal] = useState<GoalRead | "new" | null>(null);
  const [debtModal, setDebtModal] = useState<DebtRead | "new" | null>(null);
  const [isIncomeOpen, setIsIncomeOpen] = useState(false);
  const goalsQuery = useQuery({ queryKey: ["goals"], queryFn: () => api.getGoals() });
  const debtsQuery = useQuery({ queryKey: ["debts"], queryFn: () => api.getDebts() });
  const incomeQuery = useQuery({
    queryKey: ["income-events"],
    queryFn: () => api.getIncomeEvents(),
  });
  const goals = goalsQuery.data?.data ?? [];
  const debts = debtsQuery.data?.data ?? [];
  const incomeEvents = incomeQuery.data?.data ?? [];
  const currency = goals[0]?.currency ?? debts[0]?.currency ?? incomeEvents[0]?.currency ?? "USD";
  const isPending = goalsQuery.isPending || debtsQuery.isPending || incomeQuery.isPending;
  const isError = goalsQuery.isError || debtsQuery.isError || incomeQuery.isError;
  const error = goalsQuery.error ?? debtsQuery.error ?? incomeQuery.error;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Protect, then improve"
        title="Goals, debt, and future money"
        description="Minimums and essentials stay covered before extra debt or goal funding. Planned money remains distinct from posted money."
      />
      <QueryState
        isPending={isPending}
        isError={isError}
        error={error}
        onRetry={() => {
          void goalsQuery.refetch();
          void debtsQuery.refetch();
          void incomeQuery.refetch();
        }}
      >
        <Card>
          <SectionHeader
            title="Goals"
            action={
              <Button type="button" variant="secondary" onClick={() => setGoalModal("new")}>
                Add goal
              </Button>
            }
          />
          {goals.length === 0 ? (
            <StatePanel title="No goals yet">
              Create an emergency, hockey or school, irregular expense, debt payoff,
              pressure-relief, or custom goal.
            </StatePanel>
          ) : (
            <div className="goal-grid">
              {goals.map((goal) => (
                <article className="goal-card" key={goal.id}>
                  <div className="list-row">
                    <div className="list-row__main">
                      <strong>{goal.name}</strong>
                      <span>
                        {titleFromMachineValue(goal.type)}
                        {goal.targetDate ? " · " + formatHouseholdDate(goal.targetDate) : ""}
                      </span>
                    </div>
                    <Button type="button" variant="quiet" onClick={() => setGoalModal(goal)}>
                      Edit
                    </Button>
                  </div>
                  <ProgressMeter
                    label="Actual protected progress"
                    value={goal.progressPercent}
                    helper={
                      formatMoney(goal.fundedAmountMinor, goal.currency) +
                      " of " +
                      formatMoney(goal.targetAmountMinor, goal.currency)
                    }
                  />
                  {goal.nextAction ? <p className="small">{goal.nextAction}</p> : null}
                  {goal.projection ? <p className="small muted">{goal.projection}</p> : null}
                </article>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <SectionHeader
            title="Debt plan"
            action={
              <Button type="button" variant="secondary" onClick={() => setDebtModal("new")}>
                Add debt
              </Button>
            }
          />
          {debts.length === 0 ? (
            <StatePanel title="No debts added">
              Add cards, loans, or a mortgage. Missing APR remains unknown and blocks unsupported
              interest estimates.
            </StatePanel>
          ) : (
            <div className="card-list">
              {debts.map((debt) => (
                <div className="list-row" key={debt.id}>
                  <div className="list-row__main">
                    <strong>{debt.name}</strong>
                    <span>
                      Minimum {formatMoney(debt.minimumPaymentMinor, debt.currency)} · due day{" "}
                      {debt.dueDay}
                      {debt.aprBasisPoints !== undefined
                        ? " · " + String(debt.aprBasisPoints / 100) + "% APR"
                        : " · APR needs confirmation"}
                    </span>
                    {debt.nextAction ? <span>{debt.nextAction}</span> : null}
                  </div>
                  <div className="list-row__side">
                    <strong>{formatMoney(debt.balanceMinor, debt.currency)}</strong>
                    <Button type="button" variant="quiet" onClick={() => setDebtModal(debt)}>
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <SectionHeader
            title="Bonuses and irregular income"
            description="Plan before arrival; recalculate from the actual posted amount."
            action={
              <Button type="button" variant="secondary" onClick={() => setIsIncomeOpen(true)}>
                Plan income
              </Button>
            }
          />
          {incomeEvents.filter(
            (event) => event.type === "bonus" || event.type === "refund" || event.type === "other",
          ).length === 0 ? (
            <StatePanel title="No future income plans">
              Add an expected bonus, refund, or other irregular income as a range or estimate.
            </StatePanel>
          ) : (
            <div className="card-list">
              {incomeEvents
                .filter(
                  (event) =>
                    event.type === "bonus" || event.type === "refund" || event.type === "other",
                )
                .map((event) => (
                  <article className="income-plan" key={event.id}>
                    <div className="list-row">
                      <div className="list-row__main">
                        <strong>{event.sourceName}</strong>
                        <span>
                          {event.expectedDate
                            ? formatHouseholdDate(event.expectedDate) + " · "
                            : ""}
                          {titleFromMachineValue(event.confidence)}
                        </span>
                      </div>
                      <div className="list-row__side">
                        <strong>
                          {event.actualAmountMinor
                            ? formatMoney(event.actualAmountMinor, event.currency)
                            : event.expectedMinMinor && event.expectedMaxMinor
                              ? formatMoney(event.expectedMinMinor, event.currency) +
                                "–" +
                                formatMoney(event.expectedMaxMinor, event.currency)
                              : "Amount needs confirmation"}
                        </strong>
                        <StatusBadge
                          status={
                            event.status === "posted"
                              ? "plan_updated"
                              : event.status === "late"
                                ? "at_risk"
                                : "pending"
                          }
                        />
                      </div>
                    </div>
                    {event.plannedJobs.length > 0 ? (
                      <ul className="small">
                        {event.plannedJobs.map((job) => (
                          <li key={job.label}>
                            {job.label}: {formatMoney(job.plannedMinor, event.currency)} ·{" "}
                            {titleFromMachineValue(job.status)}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </article>
                ))}
            </div>
          )}
        </Card>
      </QueryState>
      <GoalModal
        isOpen={goalModal !== null}
        onClose={() => setGoalModal(null)}
        currency={currency}
        {...(goalModal && goalModal !== "new" ? { goal: goalModal } : {})}
      />
      <DebtModal
        isOpen={debtModal !== null}
        onClose={() => setDebtModal(null)}
        currency={currency}
        {...(debtModal && debtModal !== "new" ? { debt: debtModal } : {})}
      />
      <IncomeEventModal
        isOpen={isIncomeOpen}
        onClose={() => setIsIncomeOpen(false)}
        currency={currency}
      />
    </div>
  );
}

function affordabilityStatus(outcome: string): CalmStatus {
  if (
    outcome === "safe_now" ||
    outcome === "safe_with_tradeoff" ||
    outcome === "wait_until" ||
    outcome === "not_safe_current_plan" ||
    outcome === "cannot_determine"
  ) {
    return outcome;
  }
  return "cannot_determine";
}

type AffordabilityFormValues = {
  amount: string;
  sourceAccountId: string;
  purchaseDate: string;
  importance: AffordabilityRequest["importance"];
  flexibility: AffordabilityRequest["flexibility"];
  horizon: AffordabilityRequest["horizon"];
  customHorizonEndDate: string;
};

export function AffordabilityPage() {
  const api = useApi();
  const [formError, setFormError] = useState<string>();
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => api.getAccounts() });
  const { register, handleSubmit, watch } = useForm<AffordabilityFormValues>({
    defaultValues: {
      amount: "",
      sourceAccountId: "",
      purchaseDate: new Date().toISOString().slice(0, 10),
      importance: "optional",
      flexibility: "can_wait",
      horizon: "default",
      customHorizonEndDate: "",
    },
  });
  const horizon = watch("horizon");
  const mutation = useMutation({
    mutationFn: (input: AffordabilityRequest) => api.evaluateAffordability(input),
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const amountMinor = parseMoneyInput(values.amount);
    if (!amountMinor || amountMinor.startsWith("-") || amountMinor === "0") {
      setFormError("Enter a purchase amount greater than zero.");
      return;
    }
    mutation.mutate({
      amountMinor,
      sourceAccountId: values.sourceAccountId,
      purchaseDate: values.purchaseDate,
      importance: values.importance,
      flexibility: values.flexibility,
      horizon: values.horizon,
      ...(values.horizon === "custom" && values.customHorizonEndDate
        ? { customHorizonEndDate: values.customHorizonEndDate }
        : {}),
    });
  });
  const result = mutation.data?.data;
  const sourceCurrency =
    (accountsQuery.data?.data ?? []).find((account) => account.id === watch("sourceAccountId"))
      ?.currency ?? "USD";
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Decision support, not bank balance"
        title="Can I Afford It?"
        description="Test a purchase against coverage, timing, buffers, income confidence, debt, and protected priorities. The scenario does not change your live plan."
      />
      <Card>
        <form className="form-stack" onSubmit={submit}>
          <div className="form-grid form-grid--two">
            <FormField
              label={"Purchase amount (" + sourceCurrency + ")"}
              htmlFor="affordability-amount"
            >
              <TextInput
                id="affordability-amount"
                inputMode="decimal"
                placeholder="0.00"
                {...register("amount", { required: true })}
              />
            </FormField>
            <FormField label="Payment source" htmlFor="affordability-source">
              <SelectInput
                id="affordability-source"
                {...register("sourceAccountId", { required: true })}
              >
                <option value="">Choose an account or card</option>
                {(accountsQuery.data?.data ?? [])
                  .filter((account) => account.isActive)
                  .map((account) => (
                    <option value={account.id} key={account.id}>
                      {account.name}
                    </option>
                  ))}
              </SelectInput>
            </FormField>
            <FormField label="Purchase date" htmlFor="affordability-date">
              <TextInput
                id="affordability-date"
                type="date"
                {...register("purchaseDate", { required: true })}
              />
            </FormField>
            <FormField label="Importance" htmlFor="affordability-importance">
              <SelectInput id="affordability-importance" {...register("importance")}>
                <option value="essential">Essential</option>
                <option value="important">Important</option>
                <option value="optional">Optional</option>
              </SelectInput>
            </FormField>
            <FormField label="Flexibility" htmlFor="affordability-flexibility">
              <SelectInput id="affordability-flexibility" {...register("flexibility")}>
                <option value="fixed">Fixed purchase</option>
                <option value="can_wait">Can wait</option>
                <option value="can_split">Can split</option>
                <option value="alternate_source">Could use another source</option>
              </SelectInput>
            </FormField>
            <FormField label="Coverage horizon" htmlFor="affordability-horizon">
              <SelectInput id="affordability-horizon" {...register("horizon")}>
                <option value="default">Default continuous horizon</option>
                <option value="7d">7 days</option>
                <option value="next_paycheck">Next paycheck</option>
                <option value="30d">30 days</option>
                <option value="custom">Custom</option>
              </SelectInput>
            </FormField>
            {horizon === "custom" ? (
              <FormField label="Custom horizon end" htmlFor="affordability-custom-end">
                <TextInput
                  id="affordability-custom-end"
                  type="date"
                  {...register("customHorizonEndDate")}
                />
              </FormField>
            ) : null}
          </div>
          {formError ? (
            <Callout title="Check the purchase" tone="warning">
              {formError}
            </Callout>
          ) : null}
          {mutation.error ? (
            <Callout title="IterNest Money cannot evaluate this yet" tone="urgent">
              {mutation.error.message}
            </Callout>
          ) : null}
          <div className="button-row">
            <Button type="submit" isLoading={mutation.isPending}>
              Evaluate this purchase
            </Button>
          </div>
        </form>
      </Card>
      {result ? (
        <Card className="affordability-result" aria-live="polite">
          <StatusBadge status={affordabilityStatus(result.outcome)} />
          <h2>{titleFromMachineValue(result.outcome)}</h2>
          <p>{result.reason}</p>
          {result.tradeoff ? (
            <Callout title="Named tradeoff" tone="warning">
              {result.tradeoff}
            </Callout>
          ) : null}
          {result.earliestSaferDate ? (
            <Callout title="Earliest safer date" tone="info">
              {formatHouseholdDate(result.earliestSaferDate)}
            </Callout>
          ) : null}
          <div className="form-grid form-grid--two">
            <div>
              <h3>What remains covered</h3>
              {result.remainsCovered.length ? (
                <ul>
                  {result.remainsCovered.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No protected items were returned.</p>
              )}
            </div>
            <div>
              <h3>What changes</h3>
              {result.changes.length ? (
                <ul>
                  {result.changes.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No plan changes were returned.</p>
              )}
            </div>
          </div>
          <EvidenceList items={result.evidence} title="See the reservation evidence" />
          <p className="small muted">
            IterNest Money provides household organization and deterministic decision support, not
            legal, tax, investment, or individualized professional financial advice.
          </p>
        </Card>
      ) : null}
    </div>
  );
}
